import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Calendar,
} from 'lucide-react'
import { toast } from 'sonner'
import { useEmployeeContext } from './EmployeeDetailLayout'
import { requestService } from '../../services/requestService'
import { REQUEST_TYPE_LABELS, REQUEST_STATUS_LABELS } from '../../types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { formatDate } from '../../utils/dateUtils'

export function EmployeeRequestsTab() {
  const { employee, requests, isAdmin, fetchRequests } = useEmployeeContext()
  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(null)

  if (!employee) return null

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4 text-success-600" />
      case 'DENIED':
        return <XCircle className="h-4 w-4 text-error-600" />
      default:
        return <Clock className="h-4 w-4 text-warning-600" />
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'badge-success'
      case 'DENIED':
        return 'badge-error'
      default:
        return 'badge-warning'
    }
  }

  const handleDeleteRequest = async () => {
    if (!deletingRequestId) return
    try {
      await requestService.delete(deletingRequestId)
      toast.success('Solicitud eliminada')
      setDeletingRequestId(null)
      fetchRequests()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar solicitud')
    }
  }


  // Agrupar solicitudes por estado
  const pendingRequests = requests.filter(r => r.status === 'PENDING')
  const processedRequests = requests.filter(r => r.status !== 'PENDING')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Solicitudes del Empleado
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {requests.length} solicitud{requests.length !== 1 ? 'es' : ''} en total
          </p>
        </div>
        <Link
          to={`/requests/new?employeeId=${employee.id}`}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nueva Solicitud
        </Link>
      </div>

      {requests.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Este empleado no tiene solicitudes
          </p>
          <Link
            to={`/requests/new?employeeId=${employee.id}`}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Crear Primera Solicitud
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Solicitudes Pendientes */}
          {pendingRequests.length > 0 && (
            <div className="card">
              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-warning-600" />
                Pendientes
                <span className="badge badge-warning text-xs">{pendingRequests.length}</span>
              </h3>
              <div className="space-y-3">
                {pendingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <span className={`badge ${getStatusBadgeClass(req.status)}`}>
                          {getStatusIcon(req.status)}
                          <span className="ml-1">{REQUEST_STATUS_LABELS[req.status]}</span>
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {REQUEST_TYPE_LABELS[req.requestType]}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>
                            {formatDate(req.startDate)}
                            {req.secondDate
                              ? ` y ${formatDate(req.secondDate)}`
                              : req.startDate !== req.endDate
                              ? ` - ${formatDate(req.endDate)}`
                              : ''}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <span className="font-medium text-primary-600">{req.daysRequested} días</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Link
                        to={`/requests/${req.id}`}
                        className="p-2 hover:bg-warning-100 dark:hover:bg-warning-800/50 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
                        title="Ver detalle"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      {isAdmin && (
                        <>
                          <Link
                            to={`/requests/${req.id}?edit=true`}
                            className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => setDeletingRequestId(req.id)}
                            className="p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Solicitudes Procesadas */}
          {processedRequests.length > 0 && (
            <div className="card">
              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-gray-400" />
                Historial
                <span className="badge badge-gray text-xs">{processedRequests.length}</span>
              </h3>
              <div className="space-y-2">
                {processedRequests.map((req) => (
                  <Link
                    key={req.id}
                    to={`/requests/${req.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`badge ${getStatusBadgeClass(req.status)} flex-shrink-0`}>
                        {getStatusIcon(req.status)}
                        <span className="ml-1 hidden sm:inline">{REQUEST_STATUS_LABELS[req.status]}</span>
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {REQUEST_TYPE_LABELS[req.requestType]}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(req.startDate)}
                          {req.secondDate
                            ? ` y ${formatDate(req.secondDate)}`
                            : req.startDate !== req.endDate
                            ? ` - ${formatDate(req.endDate)}`
                            : ''}
                          <span className="mx-1">•</span>
                          {req.daysRequested} días
                        </p>
                      </div>
                    </div>
                    <Eye className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Request Confirm */}
      <ConfirmDialog
        isOpen={!!deletingRequestId}
        onClose={() => setDeletingRequestId(null)}
        onConfirm={handleDeleteRequest}
        title="Eliminar Solicitud"
        message="¿Estás seguro de eliminar esta solicitud? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  )
}
